import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STUDIO_STAGES, type StudioStage, type StudioState } from '@/lib/studio/studioState';

const LABELS: Record<StudioStage, string> = { plan: 'Plan', voice: 'Voice', build: 'Build', review: 'Review', deliver: 'Deliver' };

interface Props {
  stage: StudioStage;
  waiting: StudioState['waiting'];
  working: boolean;
  sceneCounts: { built: number; total: number };
  className?: string;
}

/** Plan → Voice → Build → Review → Deliver, with where the video is now. */
export function StageBar({ stage, waiting, working, sceneCounts, className }: Props) {
  const current = STUDIO_STAGES.indexOf(stage);
  const note = waiting ? 'waiting for you' : stage === 'build' && sceneCounts.total > 0 ? `${sceneCounts.built} of ${sceneCounts.total} done` : null;

  return (
    <nav aria-label="Progress" className={cn('flex items-center', className)}>
      <ol className="flex items-center gap-1 text-[13px] font-medium">
        {STUDIO_STAGES.map((s, i) => {
          const state = i < current ? 'done' : i === current ? 'current' : 'todo';
          return (
            <li key={s} data-stage={s} data-state={state} aria-current={state === 'current' ? 'step' : undefined} className="flex items-center gap-1">
              {i > 0 && <span aria-hidden="true" className="stage-sep mx-0.5 h-px w-3 bg-border" />}
              <span
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1 whitespace-nowrap',
                  state === 'done' && 'text-muted-foreground',
                  state === 'todo' && 'text-muted-foreground/70',
                  state === 'current' && 'bg-muted text-foreground',
                )}
              >
                {state === 'done' && <Check aria-hidden="true" className="h-3.5 w-3.5 text-success" />}
                {state === 'current' && (
                  <span
                    aria-hidden="true"
                    className={cn('h-1.5 w-1.5 rounded-full', waiting ? 'bg-warning' : working ? 'bg-brand motion-safe:animate-pulse' : 'bg-foreground')}
                  />
                )}
                {LABELS[s]}
                {state === 'current' && note && <span className="font-normal text-muted-foreground">· {note}</span>}
              </span>
            </li>
          );
        })}
      </ol>
      {working && !waiting && <span role="status" className="sr-only">Working on {LABELS[stage].toLowerCase()}</span>}
    </nav>
  );
}
